import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AppButton from "../../components/ui/AppButton";
import uniAdminAuthService from "../../services/university-admin/authService";
import { useNotification } from "../../context/NotificationContext";

const UMChangePasswordModal = ({ show, onClose }) => {
  const { t } = useTranslation(["universityManager", "validation", "common"]);
  const { showSuccess } = useNotification();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  if (!show) return null;

  const reset = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    setShowPasswords(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const validate = () => {
    const newErrors = {};

    if (!oldPassword) {
      newErrors.oldPassword = t("validation:required.password");
    }

    if (!newPassword) {
      newErrors.newPassword = t("validation:required.newPassword");
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t("validation:length.newPassword");
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t("validation:required.confirmPassword");
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("validation:match.password");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await uniAdminAuthService.changePassword(oldPassword, newPassword);
      if (result.success) {
        showSuccess(t("universityManager:changePasswordModal.success"));
        handleClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <h5 className="modal-title">{t("universityManager:changePasswordModal.title")}</h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">{t("universityManager:changePasswordModal.currentPasswordLabel")}</label>
                <input
                  type={showPasswords ? "text" : "password"}
                  className={`form-control ${errors.oldPassword ? "is-invalid" : ""}`}
                  value={oldPassword}
                  onChange={(e) => {
                    setOldPassword(e.target.value);
                    clearError("oldPassword");
                  }}
                  placeholder={t("universityManager:changePasswordModal.currentPasswordPlaceholder")}
                  autoFocus
                />
                {errors.oldPassword && <div className="invalid-feedback">{errors.oldPassword}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">{t("universityManager:changePasswordModal.newPasswordLabel")}</label>
                <input
                  type={showPasswords ? "text" : "password"}
                  className={`form-control ${errors.newPassword ? "is-invalid" : ""}`}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    clearError("newPassword");
                  }}
                  placeholder={t("universityManager:changePasswordModal.newPasswordPlaceholder")}
                />
                {errors.newPassword && <div className="invalid-feedback">{errors.newPassword}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label">{t("universityManager:changePasswordModal.confirmPasswordLabel")}</label>
                <input
                  type={showPasswords ? "text" : "password"}
                  className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    clearError("confirmPassword");
                  }}
                  placeholder={t("universityManager:changePasswordModal.confirmPasswordPlaceholder")}
                />
                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
              </div>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="showChangePasswordFields"
                  checked={showPasswords}
                  onChange={() => setShowPasswords(!showPasswords)}
                />
                <label className="form-check-label small" htmlFor="showChangePasswordFields">
                  {t("universityManager:changePasswordModal.showPasswords")}
                </label>
              </div>

              <div className="modal-footer border-0 px-0 pb-0">
                <AppButton variant="secondary" type="button" onClick={handleClose}>
                  {t("common:buttons.cancel")}
                </AppButton>
                <AppButton variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    t("universityManager:changePasswordModal.submitButton")
                  )}
                </AppButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UMChangePasswordModal;
